import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { api, authToken, clearSession, login, refreshAdminCache } from './api';
import { store } from './store';
import { Videos } from './Videos';
import { Voiceovers } from './Voiceovers';
import { Users } from './Users';
import { Payments } from './Payments';
import { Feedback } from './Feedback';
import { Costs } from './Costs';
import { Issues } from './Issues';
import { Tasks } from './Tasks';
import { Team } from './Team';
import { Badge, Empty, ErrorState, Loading, MeContext, Metric, ThemeToggle, date, money, shortNumber, titleCase } from './ui';
import { Icon } from './icons';
import './styles.css';

// Grouped by what the operator is doing: watching, acting, administering.
// [id, label, icon] — the id doubles as the icon name.
const NAV_GROUPS = [
  ['Monitor', [['overview', 'Overview'], ['videos', 'Videos'], ['voiceovers', 'Voiceovers'], ['users', 'Customers'], ['payments', 'Payments'], ['feedback', 'Feedback'], ['costs', 'Costs']]],
  ['Operate', [['issues', 'Issues'], ['tasks', 'Tasks']]],
  ['Admin', [['team', 'Team'], ['audit', 'Audit log']]],
];
const NAV = NAV_GROUPS.flatMap(([, items]) => items);

/** Success-rate tone for a provider row: green only when it has earned it. */
const healthTone = (p) => {
  if (!p.attempts) return '';
  const rate = p.succeeded / p.attempts;
  return rate >= 0.95 ? 'good' : rate >= 0.8 ? 'warn' : 'danger';
};

function Login({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login({ email, password });
      await onAuthenticated();
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-corner"><ThemeToggle /></div>
      <section className="login-card">
        <div className="brand"><i>C</i><span>Caption Maker</span></div>
        <h1>Sign in</h1>
        <p>Operations console — pipeline, costs, incidents and customers.</p>
        <form onSubmit={submit}>
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@captionmaker.app"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
            />
          </label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in to operations'}
          </button>
        </form>
        <small className="login-note">Access is logged and limited to authorized team members.</small>
      </section>
    </main>
  );
}

/** Renders inside the existing header action group, not as a floating overlay. */
function RefreshButton({ onRefresh, busy }) {
  const [host, setHost] = useState(null);
  useEffect(() => { setHost(document.querySelector('.header-actions')); }, []);
  if (!host) return null;
  return createPortal(
    <button className="subtle refresh-button" title="Fetch fresh data for this console" disabled={busy} onClick={onRefresh}>
      <Icon name="refresh" size={14} className={busy ? 'spin' : ''} />{busy ? 'Refreshing…' : 'Refresh'}
    </button>,
    host,
  );
}

/** Plan revenue stays per currency — nothing converts between them — so "₹4,792 · $27". */
const revenueText = (rows = []) => rows.length
  ? rows.map((r) => new Intl.NumberFormat('en-US', { style: 'currency', currency: r.currency, maximumFractionDigits: 0 }).format(r.amountMinor / 100)).join(' · ')
  : 'No plan sales yet';

function Overview({ data, onNavigate }) {
  const m = data?.metrics ?? {}; const providers = data?.providers ?? [];
  return <><section className="page-title"><div><div className="eyebrow">Overview</div><h1>Overview</h1><p>The current state of Caption Maker.</p></div><button className="subtle" onClick={() => onNavigate('videos')}>Review pipeline <Icon name="arrowRight" size={14} /></button></section>
  <section className="metric-grid overview-metrics"><Metric label="Subscribers" value={shortNumber(m.subscribers)} detail={`${m.subscribersPro ?? 0} Pro · ${m.subscribersStudio ?? 0} Studio · ${revenueText(m.planRevenueMonth)} this month`} /><Metric label="Cost today" value={money(m.costTodayUsd)} detail={`${money(m.costMonthUsd)} this month`} /><Metric label="Videos rendered" value={shortNumber(m.rendersDone)} detail={`${shortNumber(m.projectsTotal)} projects · ${shortNumber(m.projectsExpired)} past retention`} /><Metric label="Active pipeline" value={shortNumber((m.rendersActive ?? 0) + (m.transcriptionsActive ?? 0) + (m.voiceoversActive ?? 0))} detail={`${m.rendersActive ?? 0} renders · ${m.transcriptionsActive ?? 0} transcripts · ${m.voiceoversActive ?? 0} voiceovers`} /><Metric label="AI voiceovers" value={shortNumber(m.voiceoversMonth)} detail={`${Math.round((m.voiceSecondsMonth ?? 0) / 60)} min by ${m.voiceUsersMonth ?? 0} customer${m.voiceUsersMonth === 1 ? '' : 's'} this month`} /><Metric label="Script writes" value={shortNumber(m.scriptWritesMonth)} detail={`${money(m.voiceCostMonthUsd)} Groq for voice + scripts this month`} /><Metric label="Open incidents" value={shortNumber(m.incidentsOpen)} detail={`${m.incidentsUnassigned ?? 0} unassigned`} alert={m.incidentsUnassigned > 0} /><Metric label="Overdue tasks" value={shortNumber(m.tasksOverdue)} detail="Needs an owner or completion" alert={m.tasksOverdue > 0} /></section>
  <section className="content-grid"><article className="panel health-panel"><div className="panel-heading"><div><span className="eyebrow">PROVIDER HEALTH</span><h2>Last 24 hours</h2></div><button className="text-button" onClick={() => onNavigate('costs')}>Cost detail <Icon name="arrowRight" size={13} /></button></div>{providers.length ? <div className="provider-list">{providers.map((p) => <div className="provider" key={p.provider}><div className="provider-name"><span className={`dot ${healthTone(p)}`} />{p.provider}</div><div><b>{p.attempts}</b><small> attempts</small></div><div><b>{p.attempts ? Math.round((p.succeeded / p.attempts) * 100) : 0}%</b><small> success</small></div><div><b>{p.p95LatencyMs ? `${Math.round(p.p95LatencyMs / 1000)}s` : '—'}</b><small> p95</small></div><div><b>{money(p.estimatedCostUsd)}</b><small> cost</small></div></div>)}</div> : <Empty title="No provider activity in the last 24 hours" body="Usage events appear here as transcriptions and renders run." />}</article>
  <article className="panel priority-panel"><div className="panel-heading"><div><span className="eyebrow">ATTENTION QUEUE</span><h2>What needs a look</h2></div><button className="icon-button" aria-label="Open issues" title="Open issues" onClick={() => onNavigate('issues')}><Icon name="arrowUpRight" /></button></div><div className="priority-stack"><button onClick={() => onNavigate('issues')}><span className={m.incidentsUnassigned ? 'dot danger' : 'dot good'} />{m.incidentsUnassigned ?? 0} unassigned incidents <em>Review</em></button><button onClick={() => onNavigate('videos')}><span className={(m.renderFailures24h ?? 0) + (m.transcriptionFailures24h ?? 0) ? 'dot warn' : 'dot good'} />{(m.renderFailures24h ?? 0) + (m.transcriptionFailures24h ?? 0)} failures in 24h <em>Inspect</em></button><button onClick={() => onNavigate('voiceovers')}><span className={m.voiceoverFailures24h ? 'dot warn' : 'dot good'} />{m.voiceoverFailures24h ?? 0} failed voiceovers in 24h <em>Voiceovers</em></button><button onClick={() => onNavigate('tasks')}><span className={m.tasksOverdue ? 'dot danger' : 'dot good'} />{m.tasksOverdue ?? 0} overdue tasks <em>Open</em></button><button onClick={() => onNavigate('users')}><span className={m.subscriptionsEnding7d ? 'dot warn' : 'dot good'} />{m.subscriptionsEnding7d ?? 0} plans lapse within 7 days <em>Customers</em></button></div></article></section></>;
}

function App() {
  const [user, setUser] = useState(null); const [view, setView] = useState('overview'); const [overview, setOverview] = useState(null); const [loading, setLoading] = useState(true); const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  // Cross-page hand-offs: a project chosen in Videos narrows Issues; a project
  // link in an incident opens Videos searched for it.
  const [issueProject, setIssueProject] = useState(null);
  const [videoSearch, setVideoSearch] = useState('');
  // Pages that own their own fetching (see Videos.jsx etc.) must not be
  // unmounted by the shared spinner, or they lose the operator's filters.
  const selfLoading = ['videos', 'voiceovers', 'users', 'payments', 'feedback', 'costs', 'issues', 'tasks', 'team'];
  const load = async (target = view) => {
    if (selfLoading.includes(target) || target === 'audit') { setLoading(false); setError(''); return; }
    setLoading(true); setError('');
    try {
      if (target === 'overview') setOverview(await api('/overview'));
    } catch (caught) {
      if (caught.status === 401 || caught.status === 403) { clearSession(); setUser(null); } else setError(caught.message);
    } finally { setLoading(false); }
  };
  // A stored token means we are probably signed in, but `user` is null until
  // /me answers — and the login form used to render in that gap, so every
  // reload flashed a sign-in page at somebody who was already in. Hold a
  // neutral screen instead until the answer arrives.
  const [booting, setBooting] = useState(() => Boolean(authToken()));
  const authenticate = async () => { try { const me = await api('/me'); setUser(me); setView('overview'); await load('overview'); } catch (caught) { clearSession(); setUser(null); setError(caught.status === 403 ? 'Your account does not have an active admin role.' : ''); } finally { setBooting(false); } };
  useEffect(() => { if (authToken()) void authenticate(); else setLoading(false); }, []);
  const navigate = async (target) => { setView(target); setMenuOpen(false); setIssueProject(null); setVideoSearch(''); await load(target); };
  // Refresh drops every cached snapshot, then refetches the current screen
  // through the API. Self-loading pages react via useRefreshVersion().
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    refreshAdminCache();
    try { await load(view); } finally { setRefreshing(false); }
  };
  const openIssuesFor = (project) => { setView('issues'); setIssueProject(project); };
  const openVideo = (projectId) => { setView('videos'); setVideoSearch(projectId); };
  if (booting) return <main className="boot-screen"><Loading label="Signing you in…" /></main>;
  if (!user) return <Login onAuthenticated={authenticate} />;

  const content = view === 'videos' ? <Videos key={videoSearch} initialSearch={videoSearch} onOpenIssues={openIssuesFor} />
    : view === 'voiceovers' ? <Voiceovers onOpenVideos={openVideo} />
    : view === 'users' ? <Users />
    : view === 'payments' ? <Payments />
    : view === 'feedback' ? <Feedback onOpenVideos={openVideo} />
    : view === 'costs' ? <Costs />
    : view === 'issues' ? <Issues project={issueProject} onClearProject={() => setIssueProject(null)} onOpenVideos={openVideo} />
    : view === 'tasks' ? <Tasks />
    : view === 'team' ? <Team />
    : loading ? <Loading />
    : error ? <ErrorState message={error} retry={() => load(view)} />
    : view === 'overview' ? <Overview data={overview} onNavigate={navigate} />
    : <section className="page-title"><div><div className="eyebrow">Audit log</div><h1>Audit log</h1><p>Every privileged action, with who did it and when. Coming with the next API increment.</p></div></section>;

  const signOut = () => { clearSession(); setUser(null); };
  const initial = (user.user.fullName || user.user.email).slice(0, 1).toUpperCase();

  return <MeContext.Provider value={user}><div className={`app-shell ${menuOpen ? 'menu-open' : ''}`}>
    <RefreshButton onRefresh={refresh} busy={refreshing} />
    {/* The avatar here used to sign out on tap — far too easy to hit by accident.
        Sign out now lives only in the sidebar, behind the menu. */}
    <header className="mobile-bar"><button className="icon-button" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}><Icon name={menuOpen ? 'close' : 'menu'} size={18} /></button><div className="brand"><i>C</i><span>Caption Maker</span></div><ThemeToggle /><span className="avatar" title={user.user.email}>{initial}</span></header>
    {menuOpen && <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />}
    <aside className="sidebar"><div className="brand"><i>C</i><span>Caption Maker</span></div><div className="workspace"><span>WORKSPACE</span><b>Operations <em>Admin</em></b></div>
      <nav aria-label="Console">{NAV_GROUPS.map(([group, items]) => <div className="nav-group" key={group}><span className="nav-label">{group}</span>{items.map(([id, label]) => <button key={id} className={view === id ? 'active' : ''} aria-current={view === id ? 'page' : undefined} onClick={() => navigate(id)}><Icon name={id} />{label}</button>)}</div>)}</nav>
      <div className="sidebar-bottom"><div className="operator"><span>{initial}</span><div><b>{user.user.fullName || 'Operator'}</b><small>{titleCase(user.role)}</small></div><button title="Sign out" aria-label="Sign out" onClick={signOut}><Icon name="logout" /></button></div></div></aside>
    <main className="main"><header><div className="crumb">Operations <span>/</span> <b>{NAV.find(([id]) => id === view)?.[1]}</b></div><div className="header-actions"><span className="status"><i />Watchdog active</span><ThemeToggle /></div></header><div className="content">{content}</div></main>
  </div></MeContext.Provider>;
}

createRoot(document.getElementById('root')).render(<Provider store={store}><App /></Provider>);
